import * as Yup from 'yup'

import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'

import { Alert } from '@material-ui/lab'
import { Checkbox } from '../../../ui/Checkbox'
import { CsvImportButton } from '../../../ui/CsvImportButton'
import { CsvSimpleExport } from '../../../ui/CsvSimpleExport'
import InfiniteScroll from 'react-infinite-scroller'
import React from 'react'
import { SubscriberSelector } from './SubscriberSelector'
import { TruncatedId } from '../../truncated-id'
import { useFormik } from 'formik'
import { useGetPersonNameLazyQuery } from '../../../api/useGetPersonName'
import { useHistory } from 'react-router'

interface ISubscriber {
  id: string
  title: string
}

interface ISubscriberWithPossibleTitle {
  id: string
  title?: string
}

interface IForm {
  title: string
  subscribers: ISubscriber[]
  isDynamic?: boolean
  dynamicUrl?: string
}

interface IFormValues {
  title: string
  personIds: string[]
  isDynamic?: boolean
  dynamicUrl?: string
}

interface ISegmentForm {
  disabled?: boolean
  data?: IForm
  onSubmit: (values: IFormValues) => void
}

const FormSchema = Yup.object().shape({
  isDynamic: Yup.boolean(),
  dynamicUrl: Yup.string().when('isDynamic', {
    is: true,
    then: Yup.string().required('You must enter a dynamic URL')
  }),
  title: Yup.string().required('You must enter a title'),
  subscribers: Yup.array().of(
    Yup.object({
      id: Yup.string().required('You must select a subscriber id'),
      title: Yup.string().optional()
    })
  )
})

export const SegmentForm = (props: ISegmentForm) => {
  const history = useHistory()
  const fetchPerson = useGetPersonNameLazyQuery()
  const [limitSubscribers, setLimitSubscribers] = React.useState(25)

  const initialValues: IForm = props.data || {
    title: '',
    subscribers: [],
    isDynamic: false,
    dynamicUrl: ''
  }

  const formik = useFormik({
    initialValues,
    validationSchema: FormSchema,
    onSubmit: values => {
      props.onSubmit({
        personIds: values.isDynamic ? [] : values.subscribers.map(subscriber => subscriber.id),
        title: values.title,
        isDynamic: !!values.isDynamic,
        ...(values.isDynamic
          ? {
              dynamicUrl: values.dynamicUrl
            }
          : {})
      })
    }
  })

  const fetchWithRetries = async (personId: string, retries = 3): Promise<any> => {
    try {
      return await fetchPerson(personId)
    } catch (error) {
      if (retries > 0) {
        return fetchWithRetries(personId, retries - 1)
      }
      throw error
    }
  }

  const getSegmentSubscriberTitles = async (
    subscribers: ISubscriberWithPossibleTitle[],
    index: number
  ): Promise<ISubscriberWithPossibleTitle[]> => {
    const record = subscribers[index]

    if (!record) {
      return subscribers
    }

    if (record.title) {
      return await getSegmentSubscriberTitles(
        [
          ...subscribers.slice(0, index),
          {
            id: record.id,
            title: record.title
          },
          ...subscribers.slice(index + 1)
        ] as ISubscriberWithPossibleTitle[],
        index + 1
      )
    }

    try {
      const data = await fetchWithRetries(record.id)

      if (data.data.person) {
        return await getSegmentSubscriberTitles(
          [
            ...subscribers.slice(0, index),
            {
              id: record.id,
              title: `${data.data.person.firstName || ''} ${data.data.person.lastName || ''}`.trim()
            },
            ...subscribers.slice(index + 1)
          ] as ISubscriberWithPossibleTitle[],
          index + 1
        )
      } else {
        return await getSegmentSubscriberTitles(
          [
            ...subscribers.slice(0, index),
            {
              id: record.id,
              title: ''
            } as ISubscriberWithPossibleTitle,
            ...subscribers.slice(index + 1)
          ] as ISubscriberWithPossibleTitle[],
          index + 1
        )
      }
    } catch (error) {
      return await getSegmentSubscriberTitles(
        [
          ...subscribers.slice(0, index),
          {
            id: record.id,
            title: ''
          },
          ...subscribers.slice(index + 1)
        ] as ISubscriberWithPossibleTitle[],
        index + 1
      )
    }
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <FlexHorizontal>
        <Spacer size='large' />
        <CsvSimpleExport
          variant='outlined'
          title='segment-subscribers'
          data={formik.values.subscribers}
          style={{
            color: '#000'
          }}
          label='Export Subscribers'
        />
        <Box width={14} />
        <Button
          variant='contained'
          size='large'
          color='default'
          onClick={() => {
            history.push('/notifications')
          }}>
          Cancel
        </Button>
        <Box width={14} />
        <Button type='submit' variant='contained' size='large' color='primary' disabled={props.disabled}>
          Save
        </Button>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      <TableContainer
        component={Paper}
        style={{
          maxWidth: 1000
        }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell style={{ width: 220 }}>
                <Typography>Title</Typography>
              </TableCell>
              <TableCell>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  variant='outlined'
                  id='title'
                  name='title'
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
                {formik.submitCount && formik.errors.title ? (
                  <>
                    <Spacer size='small' />
                    <Alert severity='error'>{formik.errors.title}</Alert>
                  </>
                ) : null}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ width: 220 }}>
                <Typography>Is Dynamic</Typography>
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={!!formik.values.isDynamic}
                  handleCheck={() => {
                    formik.setFieldValue('isDynamic', !formik.values.isDynamic)
                  }}
                />
              </TableCell>
            </TableRow>
            {formik.values.isDynamic ? (
              <>
                <TableRow>
                  <TableCell style={{ width: 220 }}>
                    <Typography>Dynamic URL</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      disabled={props.disabled}
                      fullWidth
                      variant='outlined'
                      id='dynamicUrl'
                      name='dynamicUrl'
                      onChange={formik.handleChange}
                      value={formik.values.dynamicUrl}
                    />
                    {formik.submitCount && formik.errors.dynamicUrl ? (
                      <>
                        <Spacer size='small' />
                        <Alert severity='error'>{formik.errors.dynamicUrl}</Alert>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    valign='top'
                    style={{
                      verticalAlign: 'top'
                    }}>
                    <Typography>Subscribers</Typography>
                  </TableCell>
                  <TableCell>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <InfiniteScroll
                        loadMore={() => {
                          if (formik.values.subscribers.length > limitSubscribers) {
                            setLimitSubscribers(limitSubscribers + 25)
                          }
                        }}
                        hasMore={formik.values.subscribers.length > limitSubscribers}
                        loader={<CircularProgress />}>
                        <TableBody>
                          {formik.values.subscribers
                            .filter((_, index) => index < limitSubscribers)
                            .map(subscriber => (
                              <TableRow key={subscriber.id}>
                                <TableCell>
                                  <TruncatedId
                                    value={subscriber.id}
                                    path={`/notifications/subscriber/${subscriber.id}`}
                                    full
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </InfiniteScroll>
                    </Table>
                  </TableCell>
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell
                  valign='top'
                  style={{
                    verticalAlign: 'top'
                  }}>
                  <div>
                    <Typography>Subscribers</Typography>
                    <Spacer size='small' />
                    <CsvImportButton
                      mainButtonText='CSV Import'
                      buttonText='Add to Segment'
                      buttonVariant='primary'
                      mappings={['personId', 'name']}
                      handleImport={async records => {
                        const subscribers = await getSegmentSubscriberTitles(
                          records.map(record => ({
                            id: record.personId,
                            title: record.name
                          })) as ISubscriberWithPossibleTitle[],
                          0
                        )

                        formik.setFieldValue('subscribers', [...formik.values.subscribers, ...subscribers])
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <SubscriberSelector
                    disabled={props.disabled}
                    subscribers={formik.values.subscribers}
                    setSubscribers={subscribers => formik.setFieldValue('subscribers', subscribers)}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </form>
  )
}
